import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy, ExternalLink, Navigation } from 'lucide-react';
import {
  buildOrderFulfillmentDisplay,
  buildGoogleMapsOrderLocationLink,
  buildWhatsAppOrderLocationShareLink,
  buildWazeOrderLocationLink,
  useTranslation,
} from '@dukanti/shared';
import type { Order, TranslationKey } from '@dukanti/shared';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';

export interface OrderDetailAddressProps {
  readonly order: Order;
}

const COPY_FEEDBACK_MS = 2000;

function WhatsAppIcon(): React.JSX.Element {
  return (
    <svg aria-hidden="true" className="size-4" viewBox="0 0 32 32" fill="currentColor">
      <path d="M16.02 3.2A12.72 12.72 0 0 0 5.13 22.5L3.5 28.8l6.45-1.54A12.7 12.7 0 1 0 16.02 3.2Zm0 2.3a10.4 10.4 0 0 1 8.77 16 10.4 10.4 0 0 1-13.93 3.63l-.45-.25-3.74.9.95-3.64-.29-.47A10.43 10.43 0 0 1 16.02 5.5Zm-4.1 4.56c-.25 0-.65.1-.99.47-.34.37-1.3 1.27-1.3 3.1s1.34 3.6 1.53 3.86c.19.25 2.59 4.15 6.38 5.65 3.15 1.25 3.79 1 4.47.94.68-.06 2.2-.9 2.51-1.77.31-.87.31-1.61.22-1.77-.1-.16-.34-.25-.72-.44-.37-.19-2.2-1.09-2.54-1.21-.34-.13-.59-.19-.84.19-.25.37-.96 1.21-1.18 1.46-.22.25-.44.28-.81.09-.37-.19-1.57-.58-2.99-1.84a11.2 11.2 0 0 1-2.07-2.57c-.22-.37-.02-.57.16-.76.17-.17.37-.44.56-.66.19-.22.25-.37.37-.62.12-.25.06-.47-.03-.66-.09-.19-.84-2.02-1.15-2.77-.3-.72-.61-.62-.84-.63l-.72-.02Z" />
    </svg>
  );
}

/**
 * F-022: Shipping address section for the order detail page.
 *
 * Shows all snapshot address fields and a "Copy address" button. On copy
 * success, the button label changes to the "Copied" translation for 2000ms
 * then reverts. On failure, shows the "Copy failed" translation.
 */
export function OrderDetailAddress({ order }: OrderDetailAddressProps): React.JSX.Element {
  const { t, lang } = useTranslation();
  const { copy, isCopying } = useCopyToClipboard();
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [mapCopyState, setMapCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mapCopyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  // Clear pending feedback-reset timers on unmount so they never fire against
  // a torn-down component / environment. `mounted` also stops a copy that was
  // still awaiting the clipboard at unmount from arming a new timer.
  useEffect(() => {
    // Timer refs, not DOM refs: cleanup must read the latest pending timer.
    const timers = [copyResetTimer, mapCopyResetTimer];
    mounted.current = true;
    return () => {
      mounted.current = false;
      for (const timer of timers) if (timer.current) clearTimeout(timer.current);
    };
  }, []);
  const scheduleReset = useCallback(
    (timer: { current: ReturnType<typeof setTimeout> | null }, reset: () => void): void => {
      if (timer.current) clearTimeout(timer.current);
      if (!mounted.current) return;
      timer.current = setTimeout(() => {
        timer.current = null;
        reset();
      }, COPY_FEEDBACK_MS);
    },
    [],
  );
  const hasDeliveryLocation = order.delivery_latitude !== null && order.delivery_longitude !== null;
  const fulfillmentDisplay = buildOrderFulfillmentDisplay(order, lang);
  const deliveryDisplay =
    fulfillmentDisplay.method === 'delivery' ? fulfillmentDisplay.delivery : null;
  const locationShareIntro = t('admin.orders.detail.deliveryLocationShareMessage');
  const googleMapsLabel = t('admin.orders.detail.openGoogleMaps');
  const wazeLabel = t('admin.orders.detail.openWaze');
  const googleMapsLink = hasDeliveryLocation
    ? buildGoogleMapsOrderLocationLink({
        latitude: order.delivery_latitude as number,
        longitude: order.delivery_longitude as number,
      })
    : null;
  const wazeLink = hasDeliveryLocation
    ? buildWazeOrderLocationLink({
        latitude: order.delivery_latitude as number,
        longitude: order.delivery_longitude as number,
      })
    : null;
  const whatsappShareLink = hasDeliveryLocation
    ? buildWhatsAppOrderLocationShareLink({
        latitude: order.delivery_latitude as number,
        longitude: order.delivery_longitude as number,
        intro: locationShareIntro,
        googleMapsLabel,
        wazeLabel,
      })
    : null;

  // Build the full address text for clipboard.
  const addressLines = deliveryDisplay?.clipboardText ?? '';
  const hasAddressSnapshot =
    order.delivery_method !== 'location' && (deliveryDisplay?.detailLines ?? []).length > 0;

  async function handleCopy(): Promise<void> {
    const success = await copy(addressLines);
    setCopyState(success ? 'copied' : 'failed');
    scheduleReset(copyResetTimer, () => setCopyState('idle'));
  }

  async function handleCopyMapLink(): Promise<void> {
    if (!googleMapsLink) return;
    const success = await copy(googleMapsLink);
    setMapCopyState(success ? 'copied' : 'failed');
    scheduleReset(mapCopyResetTimer, () => setMapCopyState('idle'));
  }

  async function handleShareLocation(): Promise<void> {
    if (!whatsappShareLink) return;
    window.open(whatsappShareLink, '_blank', 'noopener,noreferrer');
  }

  const copyLabel =
    copyState === 'copied'
      ? t('admin.orders.detail.addressCopied')
      : copyState === 'failed'
        ? t('admin.orders.detail.copyFailed')
        : t('admin.orders.detail.copyAddress');
  const mapCopyLabel =
    mapCopyState === 'copied'
      ? t('admin.orders.detail.locationCopied')
      : mapCopyState === 'failed'
        ? t('admin.orders.detail.copyFailed')
        : t('admin.orders.detail.copyLocationLink');

  if (fulfillmentDisplay.method === 'pickup')
    return (
      <section
        data-testid="order-detail-pickup"
        className="border-border bg-card shadow-admin-card space-y-3 rounded-xl border p-6"
      >
        <h2 className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
          {t('fulfillment.pickupPlace')}
        </h2>
        <bdi className="text-foreground block break-words font-medium">
          {fulfillmentDisplay.name}
        </bdi>
        <bdi className="text-muted-foreground block whitespace-pre-wrap break-words text-sm">
          {fulfillmentDisplay.details}
        </bdi>
      </section>
    );

  return (
    <section
      data-testid="order-detail-address"
      className="border-border bg-card shadow-admin-card rounded-lg border p-6"
    >
      <h2 className="text-muted-foreground mb-3 text-xs font-semibold uppercase tracking-wide">
        {hasAddressSnapshot
          ? t('admin.orders.detail.deliveryInformation' as TranslationKey)
          : t('admin.orders.detail.preciseLocation')}
      </h2>

      {hasAddressSnapshot ? (
        <>
          <address className="text-foreground mb-4 text-start text-sm not-italic leading-relaxed">
            {order.address_label_snapshot && (
              <div className="text-muted-foreground mb-1 text-xs">
                {order.address_label_snapshot}
              </div>
            )}
            {(deliveryDisplay?.detailLines ?? []).map((line) => (
              <div key={line}>{line}</div>
            ))}
          </address>

          <button
            type="button"
            onClick={() => void handleCopy()}
            disabled={isCopying}
            data-testid="order-copy-address"
            className="border-border hover:bg-admin-surface-muted inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-50"
          >
            {copyState === 'copied' ? (
              <Check aria-hidden="true" className="size-4" strokeWidth={2} />
            ) : (
              <Copy aria-hidden="true" className="size-4" strokeWidth={1.75} />
            )}
            <span>{copyLabel}</span>
          </button>
        </>
      ) : null}

      <div
        data-testid="order-detail-delivery-location"
        className="border-border mt-5 border-t pt-4"
      >
        <h3 className="text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wide">
          {t('admin.orders.detail.preciseLocation')}
        </h3>
        {hasDeliveryLocation && googleMapsLink && wazeLink && whatsappShareLink ? (
          <div className="space-y-3">
            <p
              className="text-muted-foreground text-start text-sm"
              dir="ltr"
              data-testid="order-delivery-coordinates"
            >
              {order.delivery_latitude}, {order.delivery_longitude}
            </p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => void handleShareLocation()}
                data-testid="order-share-location-whatsapp"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-[#1FA855] bg-[#25D366] px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1FA855] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]/40"
              >
                <WhatsAppIcon />
                <span>{t('admin.orders.detail.shareLocationWhatsapp')}</span>
              </button>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <a
                  href={googleMapsLink}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="order-open-google-maps"
                  className="border-border hover:bg-admin-surface-muted inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium"
                >
                  <ExternalLink aria-hidden="true" className="size-4" strokeWidth={1.75} />
                  <span>{t('admin.orders.detail.openGoogleMaps')}</span>
                </a>
                <a
                  href={wazeLink}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="order-open-waze"
                  className="border-border hover:bg-admin-surface-muted inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium"
                >
                  <Navigation aria-hidden="true" className="size-4" strokeWidth={1.75} />
                  <span>{t('admin.orders.detail.openWaze')}</span>
                </a>
                <button
                  type="button"
                  onClick={() => void handleCopyMapLink()}
                  disabled={isCopying}
                  data-testid="order-copy-location-link"
                  className="border-border hover:bg-admin-surface-muted inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                >
                  {mapCopyState === 'copied' ? (
                    <Check aria-hidden="true" className="size-4" strokeWidth={2} />
                  ) : (
                    <Copy aria-hidden="true" className="size-4" strokeWidth={1.75} />
                  )}
                  <span>{mapCopyLabel}</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground text-start text-sm">
            {t('admin.orders.detail.noPreciseLocation')}
          </p>
        )}
      </div>
    </section>
  );
}
